import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Send,
  Refresh,
  ShoppingCart,
  Person,
  Add,
  Delete,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ChatBubbleOutline,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api from '../config/api';
import AgentDetailsDialog from '../components/AgentDetailsDialog';
import AdvisorInquiryForm from '../components/AdvisorInquiryForm';
import InquirySubjectTag from '../components/InquirySubjectTag';
import usePrivateTravelAvailable from '../hooks/usePrivateTravelAvailable';
import PartnerInquiryMessageThread from '../components/PartnerInquiryMessageThread';
import PartnerLogoMark from '../components/PartnerLogoMark';
import {
  getPartnerCatalogToken,
  getPackagesListingPath,
} from '../utils/partnerCatalogStorage';
import {
  DOCUMENT,
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentEyebrowSx,
} from '../constants/documentTheme';
import { partnerLinkSx, partnerOxbloodBtnSx, partnerOutlineBtnSx } from '../components/PartnerListChrome';
import {
  ALERT,
  filterVisibleInquiryMessages,
  getInquiryDisplayTitle,
  isPackageExpired,
} from '../utils/partnerListUtils';

const panelSx = {
  bgcolor: DOCUMENT.surface,
  border: `1px solid ${DOCUMENT.ruleHard}`,
  borderRadius: 0,
  height: { xs: 'auto', md: 'calc(100vh - 220px)' },
  minHeight: { xs: 360, md: 0 },
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};
const MyInquiries = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { inquiryId } = useParams();
  
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [agentDetailsOpen, setAgentDetailsOpen] = useState(false);
  const [replyMessage, setReplyMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [requestingBooking, setRequestingBooking] = useState(false);
  const [error, setError] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [inboxActionBusy, setInboxActionBusy] = useState(false);
  
  // Passenger info dialog
  const [passengerDialogOpen, setPassengerDialogOpen] = useState(false);
  const [passengers, setPassengers] = useState([]);
  const [knownAdvisors, setKnownAdvisors] = useState([]);
  const [advisorPickerOpen, setAdvisorPickerOpen] = useState(false);
  const [inquiryFormOpen, setInquiryFormOpen] = useState(false);
  const [inquiryTarget, setInquiryTarget] = useState(null);
  const [privateRequestBusy, setPrivateRequestBusy] = useState(false);
  const { available: privateTravelAvailable } = usePrivateTravelAvailable();
  
  const emptyPassenger = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    passportNumber: '',
    passportExpiry: '',
    nationality: ''
  };

  const loadKnownAdvisors = useCallback(async () => {
    try {
      const response = await api.get('/package-booking/my-advisors');
      if (response.data?.success) {
        setKnownAdvisors(response.data.advisors || []);
      }
    } catch (err) {
      console.error('Failed to load known advisors:', err);
    }
  }, []);

  const loadInquiries = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/package-booking/my-inquiries', {
        params: showArchived ? { includeArchived: 'true' } : undefined,
      });
      
      if (response.data.success) {
        setInquiries(response.data.inquiries);
        
        // If we have an inquiryId in URL, select that inquiry
        if (inquiryId) {
          const id = parseInt(inquiryId, 10);
          const found = response.data.inquiries.find(i => i.id === id);
          if (found) {
            loadInquiryMessages(found.id);
            setSelectedInquiry(found);
          } else if (!showArchived) {
            setSelectedInquiry({ id });
            loadInquiryMessages(id);
          } else {
            setSelectedInquiry(null);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load inquiries:', err);
      setError(t('myInquiries.loadFailed', { defaultValue: 'Failed to load inquiries' }));
    } finally {
      setLoading(false);
    }
  }, [inquiryId, showArchived, t]);

  const handleArchiveInquiry = async (archived = true) => {
    if (!selectedInquiry?.id) return;
    try {
      setInboxActionBusy(true);
      setError(null);
      await api.patch(`/package-booking/my-inquiries/${selectedInquiry.id}/archive`, { archived });
      setInquiries((prev) => prev.filter((item) => item.id !== selectedInquiry.id));
      setSelectedInquiry(null);
      navigate('/my-inquiries');
    } catch (err) {
      console.error('Failed to archive inquiry:', err);
      setError(
        err.response?.data?.error
        || t(archived ? 'myInquiries.archiveFailed' : 'myInquiries.restoreFailed', {
          defaultValue: archived ? 'Failed to archive inquiry' : 'Failed to restore inquiry',
        })
      );
    } finally {
      setInboxActionBusy(false);
    }
  };

  const handleDeleteInquiry = async () => {
    if (!selectedInquiry?.id) return;
    try {
      setInboxActionBusy(true);
      setError(null);
      await api.delete(`/package-booking/my-inquiries/${selectedInquiry.id}`);
      setDeleteDialogOpen(false);
      setInquiries((prev) => prev.filter((item) => item.id !== selectedInquiry.id));
      setSelectedInquiry(null);
      navigate('/my-inquiries');
    } catch (err) {
      console.error('Failed to delete inquiry:', err);
      setError(
        err.response?.data?.error
        || t('myInquiries.deleteFailed', { defaultValue: 'Failed to delete inquiry' })
      );
    } finally {
      setInboxActionBusy(false);
    }
  };

  const loadInquiryMessages = async (id) => {
    try {
      setMessagesLoading(true);
      const response = await api.get(`/package-booking/inquiry/${id}/messages`);
      
      if (response.data.success) {
        setMessages(response.data.messages);
      }
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!replyMessage.trim() || !selectedInquiry) return;
    
    try {
      setSending(true);
      await api.post(`/package-booking/inquiry/${selectedInquiry.id}/message`, {
        message: replyMessage
      });
      
      // Add message to list
      setMessages(prev => [...prev, {
        id: Date.now(),
        sender_type: 'user',
        sender_name: 'You',
        message: replyMessage,
        created_at: new Date().toISOString()
      }]);
      
      setReplyMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Open passenger dialog to collect info before booking
  const openPassengerDialog = () => {
    const numTravelers = selectedInquiry?.num_travelers || selectedInquiry?.travelers || 1;
    // Initialize passengers array with empty passenger objects
    const initialPassengers = [];
    for (let i = 0; i < numTravelers; i++) {
      initialPassengers.push({ ...emptyPassenger });
    }
    setPassengers(initialPassengers);
    setPassengerDialogOpen(true);
  };

  const updatePassenger = (index, field, value) => {
    setPassengers(prev => prev.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const addPassenger = () => {
    setPassengers(prev => [...prev, { ...emptyPassenger }]);
  };

  const removePassenger = (index) => {
    if (passengers.length > 1) {
      setPassengers(prev => prev.filter((_, i) => i !== index));
    }
  };

  const validatePassengers = () => {
    for (const p of passengers) {
      if (!p.firstName.trim() || !p.lastName.trim()) {
        return false;
      }
    }
    return passengers.length > 0;
  };

  const requestBooking = async () => {
    if (!selectedInquiry) return;
    if (!validatePassengers()) {
      setError('Please fill in at least first and last name for all passengers');
      return;
    }
    
    try {
      setRequestingBooking(true);
      const response = await api.post('/package-booking/request-booking', {
        packageId: selectedInquiry.package_id,
        inquiryId: selectedInquiry.id,
        travelers: passengers.length,
        passengersData: passengers
      });
      
      if (response.data.success) {
        setError(null);
        setPassengerDialogOpen(false);
        // Update inquiry status locally
        setInquiries(prev => prev.map(i => 
          i.id === selectedInquiry.id 
            ? { ...i, status: 'booking_requested' }
            : i
        ));
        setSelectedInquiry({ ...selectedInquiry, status: 'booking_requested' });
        
        // Navigate to bookings page
        navigate('/bookings');
      }
    } catch (err) {
      console.error('Failed to request booking:', err);
      setError('Failed to request booking');
    } finally {
      setRequestingBooking(false);
    }
  };

  useEffect(() => {
    loadInquiries();
    loadKnownAdvisors();
  }, [loadInquiries, loadKnownAdvisors]);

  const handleSelectInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    loadInquiryMessages(inquiry.id);
  };

  const openInquiryForPartner = (partner) => {
    setInquiryTarget(partner);
    setAdvisorPickerOpen(false);
    setInquiryFormOpen(true);
  };

  const handleNewRequest = () => {
    if (knownAdvisors.length > 0) {
      setAdvisorPickerOpen(true);
      return;
    }
    navigate('/advisors');
  };

  const handlePrivateRequest = async () => {
    setPrivateRequestBusy(true);
    setError(null);
    try {
      const response = await api.get('/package-booking/private-request-target');
      if (!response.data?.available) {
        setError(t('myInquiries.privateRequestUnavailable'));
        return;
      }
      setInquiryTarget({
        partnerId: response.data.partnerId,
        partnerName: response.data.partnerName,
        privateRequest: true,
      });
      setInquiryFormOpen(true);
    } catch (err) {
      console.error('Failed to open private request:', err);
      setError(err.response?.data?.error || t('myInquiries.privateRequestUnavailable'));
    } finally {
      setPrivateRequestBusy(false);
    }
  };

  const handleInquiryCreated = async (newInquiryId) => {
    setInquiryFormOpen(false);
    setInquiryTarget(null);
    try {
      const response = await api.get('/package-booking/my-inquiries');
      if (response.data.success) {
        setInquiries(response.data.inquiries);
        loadKnownAdvisors();
        const found = response.data.inquiries.find((i) => i.id === newInquiryId);
        if (found) {
          setSelectedInquiry(found);
          loadInquiryMessages(found.id);
        } else {
          setSelectedInquiry({ id: newInquiryId });
          loadInquiryMessages(newInquiryId);
        }
        navigate(`/my-inquiries/${newInquiryId}`, { replace: true });
      }
    } catch (err) {
      console.error('Failed to refresh inquiries:', err);
      setError('Failed to load inquiries');
    }
  };

  const getStatusLabel = (status) => {
    const key = `myInquiries.status.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    try {
      return formatDistanceToNow(parseISO(String(dateString).replace(' ', 'T')), { addSuffix: false });
    } catch {
      return '';
    }
  };

  const unreadCount = inquiries.reduce((sum, i) => sum + (i.unread_count || 0), 0);
  const selectedDisplayTitle = selectedInquiry
    ? getInquiryDisplayTitle(selectedInquiry, t('myInquiries.customTripRequest'))
    : '';
  const selectedExpired = selectedInquiry
    ? isPackageExpired({ available_until: selectedInquiry.package_available_until })
    : false;
  const visibleMessages = useMemo(
    () => filterVisibleInquiryMessages(
      messages,
      selectedInquiry,
      t('myInquiries.customTripRequest')
    ),
    [messages, selectedInquiry, t]
  );

  if (loading) {
    return (
      <Box sx={{ ...documentPageCanvasSx, py: 8, textAlign: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box sx={documentPageCanvasSx}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3.5, gap: 2, flexWrap: 'wrap' }}>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t('myInquiries.back')}
            sx={{ color: DOCUMENT.ink2, mt: 0.5 }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 32, md: 40 }, fontWeight: 400, lineHeight: 1.1 }}>
              {t('myInquiries.title')}
              {unreadCount > 0 ? (
                <Box
                  component="span"
                  sx={{
                    ml: 1.5,
                    fontFamily: DOCUMENT.sans,
                    fontSize: 11,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: DOCUMENT.oxblood,
                    verticalAlign: 'middle',
                  }}
                >
                  {t('myInquiries.newCount', { count: unreadCount })}
                </Box>
              ) : null}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink2, mt: 0.75, fontSize: 14.5 }}>
              {t('myInquiries.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap' }}>
            <Button
              startIcon={showArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
              onClick={() => {
                setSelectedInquiry(null);
                setShowArchived((prev) => !prev);
                navigate('/my-inquiries');
              }}
              sx={partnerOutlineBtnSx}
              data-testid="my-inquiries-archived-toggle"
            >
              {showArchived ? t('myInquiries.showActive') : t('myInquiries.showArchived')}
            </Button>
            <Button
              startIcon={<ChatBubbleOutline />}
              onClick={handleNewRequest}
              sx={partnerOxbloodBtnSx}
              data-testid="my-inquiries-new-request"
            >
              {t('myInquiries.newRequest')}
            </Button>
            {privateTravelAvailable ? (
              <Button
                onClick={handlePrivateRequest}
                disabled={privateRequestBusy}
                sx={partnerOutlineBtnSx}
                data-testid="my-inquiries-private-request"
              >
                {t('myInquiries.privateRequest')}
              </Button>
            ) : null}
            <Button startIcon={<Refresh />} onClick={loadInquiries} sx={partnerOutlineBtnSx}>
              {t('myInquiries.refresh')}
            </Button>
          </Box>
        </Box>

        {error ? (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}

        {inquiries.length === 0 && !selectedInquiry ? (
          <Box
            sx={{
              bgcolor: DOCUMENT.surface,
              border: `1px solid ${DOCUMENT.ruleHard}`,
              p: { xs: 4, md: 6 },
              textAlign: 'center',
            }}
          >
            <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 28, fontWeight: 400, mb: 1 }}>
              {showArchived ? t('myInquiries.emptyArchivedTitle') : t('myInquiries.emptyTitle')}
            </Typography>
            <Typography sx={{ color: DOCUMENT.ink3, mb: 3 }}>
              {showArchived ? t('myInquiries.emptyArchivedBody') : t('myInquiries.emptyBody')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button sx={partnerOxbloodBtnSx} onClick={() => navigate(getPackagesListingPath())}>
                {t('myInquiries.browsePackages')}
              </Button>
              {!showArchived && privateTravelAvailable ? (
                <Button
                  sx={partnerOutlineBtnSx}
                  onClick={handlePrivateRequest}
                  disabled={privateRequestBusy}
                  data-testid="my-inquiries-empty-private-request"
                >
                  {t('myInquiries.privateRequest')}
                </Button>
              ) : null}
            </Box>
          </Box>
        ) : (
          <Grid container spacing={0} sx={{ border: `1px solid ${DOCUMENT.ruleHard}`, bgcolor: DOCUMENT.surface }}>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                borderRight: { md: `1px solid ${DOCUMENT.ruleHard}` },
                borderBottom: { xs: `1px solid ${DOCUMENT.ruleHard}`, md: 0 },
              }}
            >
              <Box sx={{ ...panelSx, border: 0, height: { xs: 280, md: 'calc(100vh - 220px)' } }}>
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                  {inquiries.map((inquiry) => {
                    const current = selectedInquiry?.id === inquiry.id;
                    const expired = isPackageExpired({ available_until: inquiry.package_available_until });
                    const hasUnread = Number(inquiry.unread_count || 0) > 0;
                    const needsAttention = expired || hasUnread;
                    const displayTitle = getInquiryDisplayTitle(inquiry, t('myInquiries.customTripRequest'));
                    const destination = String(inquiry.destination || '').trim();
                    const contextLine = [
                      inquiry.booking_reference,
                      displayTitle !== inquiry.partner_name ? displayTitle : null,
                      destination && destination !== displayTitle ? destination : null,
                    ].filter(Boolean).join(' · ');
                    const statusLabel = [
                      expired ? t('myInquiries.expired') : null,
                      getStatusLabel(inquiry.status),
                      hasUnread
                        ? t('myInquiries.newCount', { count: inquiry.unread_count })
                        : null,
                    ].filter(Boolean).join(' · ');
                    const stripColor = hasUnread
                      ? ALERT
                      : (current ? DOCUMENT.accent : null);
                    return (
                      <Box
                        key={inquiry.id}
                        component="button"
                        type="button"
                        data-testid="my-inquiry-row"
                        data-unread={hasUnread ? 'true' : 'false'}
                        aria-current={current}
                        onClick={() => {
                          handleSelectInquiry(inquiry);
                          navigate(`/my-inquiries/${inquiry.id}`, { replace: true });
                        }}
                        sx={{
                          display: 'block',
                          width: '100%',
                          textAlign: 'left',
                          background: current ? '#F2ECE1' : 'none',
                          boxShadow: stripColor ? `inset 2px 0 0 ${stripColor}` : 'none',
                          border: 0,
                          borderBottom: `1px solid ${DOCUMENT.rule}`,
                          font: 'inherit',
                          color: 'inherit',
                          px: 2,
                          py: 1.15,
                          cursor: 'pointer',
                          '&:hover': { background: '#F5F0E6' },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: 13.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              minWidth: 0,
                            }}
                          >
                            {inquiry.partner_name || displayTitle}
                          </Typography>
                          <InquirySubjectTag inquiry={inquiry} t={t} />
                          <Typography sx={{ ml: 'auto', flexShrink: 0, fontSize: 11, color: DOCUMENT.ink3, fontVariantNumeric: 'tabular-nums' }}>
                            {formatTime(inquiry.last_message_at || inquiry.created_at)}
                          </Typography>
                        </Box>
                        {contextLine ? (
                          <Typography
                            sx={{
                              fontSize: 12,
                              color: DOCUMENT.ink2,
                              mt: 0.35,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {contextLine}
                          </Typography>
                        ) : null}
                        <Typography
                          sx={{
                            fontSize: 11.5,
                            mt: 0.3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            color: needsAttention ? ALERT : DOCUMENT.ink3,
                          }}
                        >
                          {statusLabel}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={8}>
              {selectedInquiry ? (
                <Box sx={{ ...panelSx, border: 0 }}>
                  <Box
                    data-testid="my-inquiry-detail-scroll"
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      WebkitOverflowScrolling: 'touch',
                      p: { xs: 2, md: '24px 28px' },
                    }}
                  >
                    <Box
                      data-testid="my-inquiry-detail-header"
                      sx={{ mb: 2.5, pb: 2, borderBottom: `1px solid ${DOCUMENT.rule}` }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                        <Typography sx={{ ...documentEyebrowSx, mb: 0 }}>
                          {[
                            selectedExpired ? t('myInquiries.expired') : null,
                            getStatusLabel(selectedInquiry.status),
                          ].filter(Boolean).join(' · ')}
                        </Typography>
                        <InquirySubjectTag inquiry={selectedInquiry} t={t} />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          gap: 2,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          component="button"
                          type="button"
                          onClick={() => setAgentDetailsOpen(true)}
                          sx={{
                            fontFamily: DOCUMENT.display,
                            fontSize: { xs: 22, md: 26 },
                            fontWeight: 400,
                            lineHeight: 1.15,
                            border: 0,
                            background: 'none',
                            p: 0,
                            cursor: 'pointer',
                            color: DOCUMENT.ink,
                            textAlign: 'left',
                            '&:hover': { color: DOCUMENT.oxblood },
                          }}
                        >
                          {selectedInquiry.partner_name || t('myInquiries.agentFallback')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                          {Number(selectedInquiry.unread_count || 0) > 0 ? (
                            <Typography sx={{ fontSize: 12, color: DOCUMENT.accent }}>
                              {t('myInquiries.newCount', { count: selectedInquiry.unread_count })}
                            </Typography>
                          ) : null}
                          <Button
                            startIcon={showArchived || selectedInquiry.user_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                            onClick={() => handleArchiveInquiry(!(showArchived || selectedInquiry.user_archived))}
                            disabled={inboxActionBusy}
                            sx={{ ...partnerOutlineBtnSx, py: 0.75 }}
                            data-testid="my-inquiry-archive"
                          >
                            {showArchived || selectedInquiry.user_archived
                              ? t('myInquiries.restore')
                              : t('myInquiries.archive')}
                          </Button>
                          <Button
                            startIcon={<Delete />}
                            onClick={() => setDeleteDialogOpen(true)}
                            disabled={inboxActionBusy}
                            sx={{ ...partnerOutlineBtnSx, py: 0.75, color: DOCUMENT.oxblood }}
                            data-testid="my-inquiry-delete"
                          >
                            {t('myInquiries.delete')}
                          </Button>
                          {selectedInquiry.status !== 'booking_requested'
                            && selectedInquiry.status !== 'converted'
                            && !selectedExpired
                            && !showArchived ? (
                            <Button
                              startIcon={<ShoppingCart />}
                              onClick={openPassengerDialog}
                              sx={{ ...partnerOxbloodBtnSx, py: 0.75 }}
                            >
                              {t('myInquiries.completeBooking')}
                            </Button>
                          ) : null}
                        </Box>
                      </Box>

                      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.6, lineHeight: 1.4 }}>
                        {[
                          selectedInquiry.requested_team_member_name
                            ? t('myInquiries.requestedAdvisorLabel') + ` ${selectedInquiry.requested_team_member_name}`
                            : null,
                          selectedInquiry.created_at ? formatTime(selectedInquiry.created_at) : null,
                          selectedInquiry.booking_reference
                            ? t('myInquiries.bookingRef', { ref: selectedInquiry.booking_reference })
                            : null,
                        ].filter(Boolean).join(' · ')}
                      </Typography>

                      <Box
                        data-testid="my-inquiry-trip-context"
                        sx={{
                          mt: 1.75,
                          px: 1.75,
                          py: 1.5,
                          border: `1px solid ${DOCUMENT.ruleHard}`,
                          backgroundColor: 'rgba(0,0,0,0.015)',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 10,
                            letterSpacing: '0.14em',
                            textTransform: 'uppercase',
                            color: DOCUMENT.ink3,
                            mb: 0.6,
                          }}
                        >
                          {t('partnerLists.tripContext')}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 15,
                            fontWeight: 600,
                            color: DOCUMENT.ink,
                            lineHeight: 1.35,
                            ...(selectedInquiry.package_slug ? {
                              cursor: 'pointer',
                              width: 'fit-content',
                              '&:hover': { color: DOCUMENT.oxblood },
                            } : {}),
                          }}
                          onClick={() => {
                            if (selectedInquiry.package_slug) {
                              navigate(`/packages/${selectedInquiry.package_slug}`);
                            }
                          }}
                          role={selectedInquiry.package_slug ? 'link' : undefined}
                        >
                          {selectedDisplayTitle}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.5, lineHeight: 1.45 }}>
                          {[
                            selectedInquiry.destination,
                            selectedInquiry.preferred_dates
                              || [selectedInquiry.preferred_travel_date, selectedInquiry.preferred_end_date]
                                .filter(Boolean)
                                .join(' – ')
                              || null,
                            (() => {
                              const travelers = Number(selectedInquiry.num_travelers || selectedInquiry.travelers || 0);
                              if (!travelers) return null;
                              return t('partnerLists.travelerCount', { count: travelers });
                            })(),
                            selectedInquiry.duration_days
                              ? t('myInquiries.days', { count: selectedInquiry.duration_days })
                              : null,
                          ].filter(Boolean).join(' · ')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                          {selectedInquiry.package_slug ? (
                            <Box
                              component="button"
                              type="button"
                              sx={partnerLinkSx}
                              onClick={() => navigate(`/packages/${selectedInquiry.package_slug}`)}
                            >
                              {t('myInquiries.viewPackage')}
                            </Box>
                          ) : null}
                          {selectedInquiry.booking_reference ? (
                            <Box
                              component="button"
                              type="button"
                              sx={partnerLinkSx}
                              onClick={() => navigate(`/my-bookings/packages/${selectedInquiry.booking_reference}`)}
                            >
                              {t('myInquiries.viewBooking')}
                            </Box>
                          ) : null}
                        </Box>
                      </Box>
                    </Box>

                    {messagesLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={28} sx={{ color: DOCUMENT.oxblood }} />
                      </Box>
                    ) : (
                      <PartnerInquiryMessageThread
                        messages={visibleMessages}
                        partnerName={selectedInquiry.partner_name}
                        viewer="traveler"
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      p: { xs: 2, md: 2.25 },
                      borderTop: `1px solid ${DOCUMENT.rule}`,
                      bgcolor: DOCUMENT.surface,
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: 10, letterSpacing: '0.19em', textTransform: 'uppercase', color: DOCUMENT.ink3, display: 'block', mb: 1.1 }}>
                      {t('partnerLists.reply')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
                      <Box
                        component="textarea"
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                          }
                        }}
                        placeholder={t('myInquiries.messagePlaceholder')}
                        sx={{
                          width: '100%',
                          background: 'transparent',
                          border: `1px solid ${DOCUMENT.ruleHard}`,
                          font: 'inherit',
                          fontSize: 14.5,
                          lineHeight: 1.65,
                          p: '12px 14px',
                          minHeight: 72,
                          resize: 'none',
                          color: DOCUMENT.ink,
                          outline: 0,
                          '&:focus': { borderColor: DOCUMENT.accent },
                          '&::placeholder': { color: DOCUMENT.ink3 },
                        }}
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={!replyMessage.trim() || sending}
                        sx={{ ...partnerOxbloodBtnSx, minWidth: 52, px: 1.75, alignSelf: 'stretch' }}
                        aria-label={t('myInquiries.send')}
                      >
                        {sending ? <CircularProgress size={18} sx={{ color: DOCUMENT.ivory }} /> : <Send fontSize="small" />}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    ...panelSx,
                    border: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 4,
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 24, color: DOCUMENT.ink2, mb: 1 }}>
                    {t('myInquiries.selectTitle')}
                  </Typography>
                  <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14 }}>
                    {t('myInquiries.selectBody')}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'center', flexWrap: 'wrap', mt: 2.5 }}>
                    <Button
                      startIcon={<ChatBubbleOutline />}
                      onClick={handleNewRequest}
                      sx={partnerOxbloodBtnSx}
                      data-testid="my-inquiries-select-new-request"
                    >
                      {t('myInquiries.newRequest')}
                    </Button>
                    {privateTravelAvailable ? (
                      <Button
                        onClick={handlePrivateRequest}
                        disabled={privateRequestBusy}
                        sx={partnerOutlineBtnSx}
                        data-testid="my-inquiries-select-private-request"
                      >
                        {t('myInquiries.privateRequest')}
                      </Button>
                    ) : null}
                  </Box>
                </Box>
              )}
            </Grid>
          </Grid>
        )}

      <Dialog
        open={advisorPickerOpen}
        onClose={() => setAdvisorPickerOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { bgcolor: DOCUMENT.surface, borderRadius: 0 } }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 24 }}>
          {t('myInquiries.pickAdvisorTitle')}
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography sx={{ color: DOCUMENT.ink3, fontSize: 14, mb: 2 }}>
            {t('myInquiries.pickAdvisorBody')}
          </Typography>
          {knownAdvisors.map((advisor) => (
            <Button
              key={advisor.partnerId}
              fullWidth
              onClick={() => openInquiryForPartner(advisor)}
              sx={{
                justifyContent: 'flex-start',
                gap: 1.5,
                py: 1.25,
                mb: 1,
                border: `1px solid ${DOCUMENT.rule}`,
                color: DOCUMENT.ink,
                textTransform: 'none',
                '&:hover': { borderColor: DOCUMENT.accent, bgcolor: DOCUMENT.canvas },
              }}
              data-testid={`my-inquiries-pick-advisor-${advisor.partnerId}`}
            >
              <PartnerLogoMark
                src={advisor.logo}
                partner={{ company_name: advisor.partnerName }}
                alt=""
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  bgcolor: DOCUMENT.canvas,
                  border: `1px solid ${DOCUMENT.rule}`,
                  color: DOCUMENT.oxblood,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              />
              <Typography sx={{ fontWeight: 500 }}>{advisor.partnerName}</Typography>
            </Button>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => navigate('/advisors')} sx={partnerLinkSx}>
            {t('myInquiries.browseAdvisors')}
          </Button>
          <Button onClick={() => setAdvisorPickerOpen(false)} sx={partnerOutlineBtnSx}>
            {t('myInquiries.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <AdvisorInquiryForm
        open={inquiryFormOpen}
        onClose={() => {
          setInquiryFormOpen(false);
          setInquiryTarget(null);
        }}
        mode="general"
        partnerId={inquiryTarget?.partnerId}
        partnerName={inquiryTarget?.partnerName}
        teamMembers={inquiryTarget?.teamMembers}
        catalogToken={getPartnerCatalogToken()}
        privateRequest={Boolean(inquiryTarget?.privateRequest)}
        onInquiryCreated={handleInquiryCreated}
      />

      {/* Passenger Info Dialog */}
      <Dialog
        open={passengerDialogOpen}
        onClose={() => setPassengerDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: DOCUMENT.surface, borderRadius: 0 } }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400, fontSize: 26 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Person sx={{ color: DOCUMENT.oxblood }} />
            {t('myInquiries.passengerTitle')}
          </Box>
          <Typography sx={{ fontFamily: DOCUMENT.sans, fontSize: 13.5, color: DOCUMENT.ink3, mt: 0.75 }}>
            {t('myInquiries.passengerBody')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: DOCUMENT.rule }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          ) : null}

          {passengers.map((passenger, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography sx={{ fontWeight: 600 }}>
                  {t('myInquiries.passengerN', { n: index + 1 })}
                </Typography>
                {passengers.length > 1 ? (
                  <IconButton size="small" color="error" onClick={() => removePassenger(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                ) : null}
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.firstName')}
                    value={passenger.firstName}
                    onChange={(e) => updatePassenger(index, 'firstName', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.lastName')}
                    value={passenger.lastName}
                    onChange={(e) => updatePassenger(index, 'lastName', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.email')}
                    type="email"
                    value={passenger.email}
                    onChange={(e) => updatePassenger(index, 'email', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.phone')}
                    value={passenger.phone}
                    onChange={(e) => updatePassenger(index, 'phone', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.dob')}
                    type="date"
                    value={passenger.dateOfBirth}
                    onChange={(e) => updatePassenger(index, 'dateOfBirth', e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('myInquiries.gender')}</InputLabel>
                    <Select
                      value={passenger.gender}
                      label={t('myInquiries.gender')}
                      onChange={(e) => updatePassenger(index, 'gender', e.target.value)}
                    >
                      <MenuItem value="male">{t('myInquiries.genderMale')}</MenuItem>
                      <MenuItem value="female">{t('myInquiries.genderFemale')}</MenuItem>
                      <MenuItem value="other">{t('myInquiries.genderOther')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.nationality')}
                    value={passenger.nationality}
                    onChange={(e) => updatePassenger(index, 'nationality', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.passportNumber')}
                    value={passenger.passportNumber}
                    onChange={(e) => updatePassenger(index, 'passportNumber', e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('myInquiries.passportExpiry')}
                    type="date"
                    value={passenger.passportExpiry}
                    onChange={(e) => updatePassenger(index, 'passportExpiry', e.target.value)}
                    size="small"
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </Grid>

              {index < passengers.length - 1 ? <Divider sx={{ mt: 3, borderColor: DOCUMENT.rule }} /> : null}
            </Box>
          ))}

          <Button startIcon={<Add />} onClick={addPassenger} sx={{ ...partnerLinkSx, mt: 1 }}>
            {t('myInquiries.addPassenger')}
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPassengerDialogOpen(false)} sx={partnerLinkSx}>
            {t('myInquiries.cancel')}
          </Button>
          <Button
            onClick={requestBooking}
            disabled={requestingBooking || !validatePassengers()}
            startIcon={requestingBooking ? <CircularProgress size={16} color="inherit" /> : <ShoppingCart />}
            sx={partnerOxbloodBtnSx}
          >
            {requestingBooking ? t('myInquiries.processing') : t('myInquiries.confirmBooking')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => !inboxActionBusy && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
          {t('myInquiries.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: DOCUMENT.ink2, fontSize: 14.5 }}>
            {t('myInquiries.deleteBody')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={inboxActionBusy}
            sx={partnerLinkSx}
          >
            {t('myInquiries.cancel')}
          </Button>
          <Button
            onClick={handleDeleteInquiry}
            disabled={inboxActionBusy}
            sx={{ ...partnerOxbloodBtnSx }}
            data-testid="my-inquiry-delete-confirm"
          >
            {inboxActionBusy ? <CircularProgress size={16} sx={{ color: DOCUMENT.ivory }} /> : t('myInquiries.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <AgentDetailsDialog
        open={agentDetailsOpen}
        onClose={() => setAgentDetailsOpen(false)}
        agent={selectedInquiry}
      />
      </Container>
    </Box>
  );
};

export default MyInquiries;

